import { Cartesian2 } from "cesium";
import { HALinearSegment } from "./horizontal-alignment-linear-segment";
import { CurveDataType, HorizontalAlignmentType, } from "./interface";
import * as HAUtils from "./horizontal-alignment-utils";
/**
 * Tangent Alignment
 * This is the simplest form of horizontal alignment, consisting of one or more straight line segments.
 * Technical Name: Polyline or Tangent Alignment.
 * Characteristics: In this design, the direction (bearing) of the road remains constant between two points
 */
export class TangentHorizontalAlignment {
    type = HorizontalAlignmentType.TANGENT;
    // -------------------- Private Instance Variables --------------------
    _rootHACurve;
    _points;
    _length;
    constructor() {
        this._rootHACurve = undefined;
        this._points = [];
        this._length = 0;
    }
    //-------------------- Public Getters/Setters --------------------
    get points() {
        return this._points;
    }
    get segments() {
        return this._rootHACurve?.toArray() || [];
    }
    get length() {
        return this._length;
    }
    //----------------------- Public Methods -----------------------
    dispose() {
        let currentSegment = this._rootHACurve;
        while (currentSegment) {
            const next = currentSegment.next;
            currentSegment.dispose();
            currentSegment = next;
        }
        this._rootHACurve = undefined;
        this._points.length = 0;
        this._length = 0;
    }
    recompute(_ipPoints) {
        let currentSegment = this._rootHACurve;
        // this will not change the type of segment, it will just recompute the element
        while (currentSegment) {
            currentSegment.recomputeElement();
            currentSegment = currentSegment.next;
        }
        this.postCompute();
    }
    initializeFromIPPoints(ipPoints) {
        this.dispose();
        if (ipPoints.length < 2) {
            throw new Error("At least two IP points are required");
        }
        const linearSegments = [];
        for (let i = 0; i < ipPoints.length - 1; i++) {
            linearSegments.push(new HALinearSegment(ipPoints[i], ipPoints[i + 1]));
        }
        this._rootHACurve = linearSegments[0];
        for (let i = 1; i < linearSegments.length; i++) {
            linearSegments[i - 1].insertAfter(linearSegments[i]);
        }
        this.postCompute();
        return this._rootHACurve;
    }
    initializeFromCurveData(curveData, surveyPointsMap) {
        this.dispose();
        if (curveData.length === 0) {
            throw new Error("Curve data is empty");
        }
        const getSegment = (data) => {
            switch (data.type) {
                case CurveDataType.LINEAR: {
                    const startPoint = surveyPointsMap.get(data.startPointId);
                    const endPoint = surveyPointsMap.get(data.endPointId);
                    if (!startPoint || !endPoint) {
                        throw new Error("Survey point not found");
                    }
                    return new HALinearSegment(startPoint, endPoint);
                }
                default:
                    throw new Error(`Unknown curve type: ${data.type}`);
            }
        };
        this._rootHACurve = getSegment(curveData[0]);
        let currentSegment = this._rootHACurve;
        for (let i = 1; i < curveData.length; i++) {
            const segment = getSegment(curveData[i]);
            currentSegment.insertAfter(segment);
            currentSegment = segment;
        }
        this.postCompute();
    }
    getClosestPointInfo(point) {
        return HAUtils.getClosestPointInfo(point, this);
    }
    getInfoAtDistanceInBatch(sortedDistanceFromStart) {
        return HAUtils.getInfoAtDistanceInBatch(sortedDistanceFromStart, this);
    }
    getPointInfoAtDistance(distanceFromStart) {
        return HAUtils.getPointInfoAtDistance(distanceFromStart, this);
    }
    getDistanceFromStart(point) {
        return HAUtils.getDistanceFromStart(point, this);
    }
    // -------------------------- Private Methods --------------------------
    computePoints() {
        this._points.length = 0;
        let currentSegment = this._rootHACurve;
        while (currentSegment) {
            this._points.push(...currentSegment.curvePoints);
            currentSegment = currentSegment.next;
        }
    }
    computeLength() {
        this._length = 0;
        for (let i = 0; i < this.points.length - 1; i++) {
            this._length += Cartesian2.distance(this.points[i], this.points[i + 1]);
        }
    }
    postCompute() {
        this.computePoints();
        this.computeLength();
    }
}
//# sourceMappingURL=tangent-horizontal-alignment.js.map